import React, { useCallback, useContext, useEffect, useState } from 'react';
import './workflowEditor.css';

import {
  deleteTenantSubworkflow,
  deleteTenantWorkflow,
  getWorkflowLatestRevision,
  updateTenantSubworkflow,
} from '../../services/api-calls';
import {
  createLocaleSpecifierFromParts,
  parseLocaleSpecifier,
  saveSubworkflows,
  validateSubworkflow,
} from '../../services/utils';

import AddSubworkflowModal from '../modals/AddSubworkflow';
import ConfirmationDialog from '../../../../global/confirmation-dialog/ConfirmationDialog';
import ErrorDialog from '../modals/ErrorDialog';
import { Map as ImmutableMap } from 'immutable';
import { TenantIdContext, AccessGroupsContext } from '../../../../components/app/App';
import { WorkflowGraph } from './workflow-graph';
import GenericDialog from '../../../../global/generic-dialog/GenericDialog';
import _ from 'lodash';
import { updateTenantWorkflowAttributes } from '../../services/api-calls';
import { useHistory } from 'react-router';
import Alert from '@amzn/meridian/alert';
import Link from '@amzn/meridian/link';
import { ManageWorkflowButtons } from './ManageWorkflowButtons';
import { Header } from '../header/Header';
import { WorkflowDisplay } from './WorkflowDisplay';
import { buildSelectionAttributes } from '../../services/utils';
import { RESTRICTED_DAYS_END, WORKFLOW_EDIT_ACCESS } from '../../../../global/app-global-values';
import { isBlockedRestricted } from '../../../../global/change-control-utils';
export const WorkflowEditor = ({ match, onWorkflowDeleted }) => {
  const tenantId = useContext(TenantIdContext);
  const [addingSubworkflow, setAddingSubworkflow] = useState(false);
  const [newSubworkflows, setNewSubworkflows] = useState([]);
  const [workflowGraphs, setWorkflowGraphs] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [errors, setErrors] = useState(null);

  const [isDeleteConfirmationDialogOpen, setIsDeleteConfirmationDialogOpen] = useState(false);
  const [deleteConfirmationCallback, setDeleteConfirmationCallback] = useState(() => {});

  const [workflowData, setWorkflowData] = useState(null);
  const [initialWorkflowSelectionAttributes, setInitialWorkflowSelectionAttributes] = useState(new ImmutableMap());
  const [workflowSelectionAttributes, setWorkflowSelectionAttributes] = useState(new ImmutableMap());
  const [loadedTenantId, setLoadedTenantId] = useState('');
  const history = useHistory();
  const accessGroups = useContext(AccessGroupsContext);
  const [editable, setEditable] = useState(accessGroups.includes(WORKFLOW_EDIT_ACCESS));
  const [pendingPromoSubData, setPendingPromoSubData] = useState(null);
  const [showBlockedRestrictedDialog, setShowBlockedRestrictedDialog] = useState(false);

  const _save = useCallback(() => {
    let needsTesting = false;
    let hasErrors = false;
    const errorMap = new Map();
    for (const sub of workflowData.subWorkflows) {
      needsTesting = needsTesting || !sub.isTested;
      const errors = validateSubworkflow(sub);
      if (errors.length > 0) {
        hasErrors = true;
        errorMap.set(createLocaleSpecifierFromParts(sub.sourceLocale, sub.targetLocale), errors);
      }
    }
    if (hasErrors) {
      setErrors(errorMap);
      return;
    }

    saveSubworkflows(tenantId, workflowData, workflowGraphs, newSubworkflows).then(result => {
      if (!result) {
        return;
      }
      setNewSubworkflows([]);

      if (
        initialWorkflowSelectionAttributes &&
        workflowSelectionAttributes &&
        !initialWorkflowSelectionAttributes.equals(workflowSelectionAttributes)
      ) {
        const newAttributes = workflowSelectionAttributes.mapEntries(([k, v]) => [v.name, v.value]).toMap();
        updateTenantWorkflowAttributes(tenantId, workflowData.workflowId, newAttributes).then(() => {
          setInitialWorkflowSelectionAttributes(workflowSelectionAttributes);
        });
      }
    });
  }, [
    initialWorkflowSelectionAttributes,
    newSubworkflows,
    tenantId,
    workflowData,
    workflowGraphs,
    workflowSelectionAttributes,
  ]);

  const handleSaveClicked = useCallback(() => {
    _save();
  }, [_save]);

  useEffect(() => {
    if (loadedTenantId && loadedTenantId !== tenantId) {
      return;
    }
    getWorkflowLatestRevision(tenantId, match.params.id).then(response => {
      const workflowFromResponse = response?.data;
      if (!workflowFromResponse || !workflowFromResponse?.workflowId || !workflowFromResponse?.workflowName) {
        history.push('/workflows');
      }
      const selectionAttributesFromResponse = buildSelectionAttributes(workflowFromResponse?.selectorAttributes);
      setInitialWorkflowSelectionAttributes(selectionAttributesFromResponse);
      setWorkflowSelectionAttributes(selectionAttributesFromResponse);
      setWorkflowData(workflowFromResponse);
      setLoadedTenantId(tenantId);
    });
  }, [match, tenantId, history, loadedTenantId]);

  const resetTenantWorkflowById = () => {
    getWorkflowLatestRevision(tenantId, match.params.id).then(response => {
      setWorkflowData(response.data);
    });
  };

  const handlePromoteToProduction = useCallback(
    subData => {
      if (isBlockedRestricted()) {
        setPendingPromoSubData(subData);
        setShowBlockedRestrictedDialog(true);
        return;
      }
      updateTenantSubworkflow(tenantId, workflowData.workflowId, subData, true);
    },
    [tenantId, workflowData]
  );

  const handleBlockedRestrictedPromotionOverride = useCallback(() => {
    updateTenantSubworkflow(tenantId, workflowData.workflowId, pendingPromoSubData, true);
    setPendingPromoSubData(null);
    setShowBlockedRestrictedDialog(false);
  }, [tenantId, workflowData, pendingPromoSubData]);

  const handleDeleteWorkflow = () => {
    deleteTenantWorkflow(tenantId, workflowData.workflowId).then(response => {
      if (response) {
        history.push('/workflows');
        onWorkflowDeleted(workflowData.workflowId);
      }
    });
  };

  const deleteConfirmation = () => {
    setDeleteConfirmationCallback(() => () => handleDeleteWorkflow());
    setIsDeleteConfirmationDialogOpen(true);
  };

  const handleDeleteSubworkflow = (sourceLocale, targetLocale) => {
    if (_tryLocalDeleteSubworkflow(sourceLocale, targetLocale)) {
      return;
    }
    deleteTenantSubworkflow(
      tenantId,
      workflowData.workflowId,
      createLocaleSpecifierFromParts(sourceLocale, targetLocale)
    ).then(response => {
      if (response) {
        resetTenantWorkflowById();
      }
    });
  };

  const _tryLocalDeleteSubworkflow = (sourceLocale, targetLocale) => {
    const newSubs = newSubworkflows;
    const newWd = _.cloneDeep(workflowData);
    for (const [index, sub] of newSubworkflows.entries()) {
      const [source, target] = parseLocaleSpecifier(sub);
      if (source === sourceLocale && target === targetLocale) {
        const [, indexToRemove] = _findSubworkflow(sub);
        let left = workflowData.subWorkflows.slice(0, indexToRemove);
        let right = workflowData.subWorkflows.slice(indexToRemove + 1);
        newWd.subWorkflows = [...left, ...right];
        left = workflowGraphs.slice(0, indexToRemove);
        right = workflowGraphs.slice(indexToRemove + 1);
        setWorkflowGraphs([...left, ...right]);
        newSubs.splice(index, 1);
        setWorkflowData(newWd);
        setNewSubworkflows(newSubs);
        return true;
      }
    }
    return false;
  };

  const handleAddSubworkflow = () => {
    setAddingSubworkflow(true);
  };

  const handleAddSubworkflowCancel = () => {
    setAddingSubworkflow(false);
  };

  const handleCloseErrorDialog = () => {
    setErrors(null);
  };

  const handleAddSubworkflowAccept = (source, target, copyFrom) => {
    setAddingSubworkflow(false);
    const [fromSub] = _findSubworkflow(copyFrom);
    let newSub;
    if (fromSub) {
      newSub = _.cloneDeep(fromSub);
      newSub.sourceLocale = source;
      newSub.targetLocale = target;
    } else {
      newSub = _createNewSubworkflow(source, target);
    }
    workflowData.subWorkflows.push(newSub);
    setNewSubworkflows([...newSubworkflows, createLocaleSpecifierFromParts(source, target)]);
    setWorkflowGraphs([...workflowGraphs, new WorkflowGraph(newSub)]);
  };

  const _createNewSubworkflow = (source, target) => {
    return {
      startAt: 0,
      sourceLocale: source,
      targetLocale: target,
      steps: [
        {
          id: 0,
          type: 'SEGMENT_AND_FILTER',
          parameters: {
            filterRule: 'regex',
          },
          next: 1,
        },
        {
          id: 1,
          type: 'MERGE',
          parameters: {
            filterRule: 'regex',
          },
          next: -1,
        },
      ],
    };
  };

  const handleGraphUpdated = (source, target, graph) => {
    const specifier = createLocaleSpecifierFromParts(source, target);
    const [sub] = _findSubworkflow(specifier);
    sub.startAt = graph.startAt;
    sub.steps = graph.getSteps();
  };

  const _findSubworkflow = localeSpecifier => {
    const [source, target] = parseLocaleSpecifier(localeSpecifier);
    for (const [index, sub] of workflowData.subWorkflows.entries()) {
      if (sub.sourceLocale === source && sub.targetLocale === target) {
        return [sub, index];
      }
    }
    // Need to return as an array here too, to allow for array assignment of return values
    // e.g. [sub, index] = _findSubworkflow(specifier)
    // Using -1 as the index for consistency with indexof()
    return [null, -1];
  };

  useEffect(() => {
    if (!workflowData || !workflowData.subWorkflows) {
      return;
    }
    setWorkflowGraphs(
      workflowData.subWorkflows.map(subWorkflow => {
        return new WorkflowGraph(subWorkflow);
      })
    );
  }, [workflowData]);

  const handleEnableEditing = useCallback(() => {
    setEditable(true);
  }, []);

  const handleWorkflowAttributesChanged = useCallback(
    attributes => {
      workflowData.isTested = false;
      setWorkflowSelectionAttributes(attributes);
    },
    [workflowData]
  );

  const BlockedBanner = () => {
    if (!isBlockedRestricted()) {
      return null;
    }

    let msg = `No changes to workflows or A/B tests should be made until ${RESTRICTED_DAYS_END.toLocaleString()}`;

    return (
      <Alert type="warning">
        {msg} due to{' '}
        <Link
          href="https://w.amazon.com/bin/view/ChangeControlPolicy/Calendar/2024/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Restricted and Blocked Days
        </Link>
        . If a workflow is broken and you need to make a change, coordinate with{' '}
        <Link href="https://oncall.corp.amazon.com/#/view/cs-blaze/schedule" target="_blank" rel="noopener noreferrer">
          CSAI-Training On Call
        </Link>{' '}
        to make the change.
      </Alert>
    );
  };

  return (
    <div className="w-100 h-100 p-1 d-flex flex-column">
      <Header workflowData={workflowData} currentPage="edit" />
      <BlockedBanner />
      <WorkflowDisplay
        workflowData={workflowData}
        workflowGraphs={workflowGraphs}
        workflowSelectionAttributes={workflowSelectionAttributes}
        editable={editable}
        onDeleteSubworkflow={handleDeleteSubworkflow}
        onGraphUpdated={handleGraphUpdated}
        onWorkflowAttributesChanged={handleWorkflowAttributesChanged}
        onPromoteToProduction={handlePromoteToProduction}
        onSave={handleSaveClicked}
      />
      <ManageWorkflowButtons
        editable={editable}
        onDelete={deleteConfirmation}
        onRevert={resetTenantWorkflowById}
        onSave={handleSaveClicked}
        onEnableEditing={handleEnableEditing}
        onAddSubworkflow={handleAddSubworkflow}
        hasSubworkflows={workflowData && workflowData.subWorkflows.length > 0}
      />
      {editable ? (
        <AddSubworkflowModal
          open={addingSubworkflow}
          existingSubworkflows={workflowData && workflowData.subWorkflows}
          onCancel={handleAddSubworkflowCancel}
          onAccept={handleAddSubworkflowAccept}
        />
      ) : null}
      <ConfirmationDialog
        isOpen={isDeleteConfirmationDialogOpen}
        setIsOpen={setIsDeleteConfirmationDialogOpen}
        text={'Are you sure you would like to remove this workflow?'}
        onConfirm={deleteConfirmationCallback}
      />
      <GenericDialog
        isOpen={showSaveModal}
        setIsOpen={setShowSaveModal}
        isConfirmDisabled={false}
        text="There are unsaved changes. Would you like to save them now?"
        title="Unsaved Changed"
        actionButtonCaption="Save and Go to Testing"
        cancelButtonCaption="Cancel"
        onConfirm={handleSaveClicked}
      />
      <GenericDialog
        isOpen={showBlockedRestrictedDialog}
        setIsOpen={setShowBlockedRestrictedDialog}
        isConfirmDisabled={false}
        text="Blocked or Restricted Days are in effect. Have you coordinated with Rosetta On Call to make the change?"
        title="Blocked and Restricted Days"
        actionButtonCaption="Promote to Production"
        cancelButtonCaption="Cancel"
        onConfirm={handleBlockedRestrictedPromotionOverride}
      />
      <ErrorDialog errors={errors} onClose={handleCloseErrorDialog} />
    </div>
  );
};
